import { useState, useEffect, useCallback } from 'react';

interface UseSessionTimeoutOptions {
  timeoutMs?: number; // Timeout in milliseconds
  onTimeout?: () => void; // Callback when timeout occurs
}

interface UseSessionTimeoutReturn {
  isTimedOut: boolean;
  resetTimeout: () => void;
  clearSessionTimeout: () => void;
}

const useSessionTimeout = (
  options: UseSessionTimeoutOptions = {}
): UseSessionTimeoutReturn => {
  const { timeoutMs = 300000, onTimeout } = options; // Default 5 minutes
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const resetTimeout = useCallback(() => {
    // This function is now handled by the activity handler in useEffect
    // We keep it for external use but the main logic is in the effect
    setIsTimedOut(false);
  }, []);

  const clearSessionTimeout = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsTimedOut(false);
  }, []);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    let currentTimeoutId: NodeJS.Timeout | null = null;

    const handleActivity = () => {
      // Clear existing timeout
      if (currentTimeoutId) {
        clearTimeout(currentTimeoutId);
      }

      // Reset timeout state
      setIsTimedOut(false);

      // Set new timeout
      currentTimeoutId = setTimeout(() => {
        setIsTimedOut(true);
        onTimeout?.();
      }, timeoutMs);
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial timeout setup
    handleActivity();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (currentTimeoutId) {
        clearTimeout(currentTimeoutId);
      }
    };
  }, [timeoutMs]); // Removed onTimeout from dependencies

  return {
    isTimedOut,
    resetTimeout,
    clearSessionTimeout,
  };
};

export default useSessionTimeout;
